import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, RotateCcw, Save, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import { useToast } from '@/components/ui/toast';
import { markerVisual } from '@/components/maps/map-identity';

const roles = ['road_captain', 'club_captain', 'sub_convoy_leader', 'member', 'sweeper', 'medic', 'mechanic', 'marshal', 'guest'];
const colors = ['#42e8e0', '#a78bfa', '#f4b942', '#fb7185', '#34d399', '#60a5fa'];

function MarkerPreview({ role, state, identity }) {
  const properties = {
    rideRole: role,
    connectivityStage: state === 'warning' ? 'reconnecting' : state === 'critical' ? 'emergency_active' : state === 'offline' ? 'signal_weak' : 'online',
    rideStatus: state === 'critical' ? 'emergency' : state === 'offline' ? 'offline' : 'riding',
    isSpeaking: state === 'speaking',
    clubId: 'preview-club',
  };
  const visual = markerVisual(properties, identity);
  return <div className={`identity-marker identity-marker--${visual.state}`} style={{ '--identity-color': visual.club.color }}>
    <span className="identity-marker__halo"/>
    <span className={`identity-marker__ring identity-marker__ring--${identity.pattern}`}/>
    <span className="identity-marker__core" dangerouslySetInnerHTML={{ __html: visual.glyph }}/>
    <small>{visual.role.label}</small>
  </div>;
}

export function MapIdentityPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [identity, setIdentity] = useState({ color: '#42e8e0', pattern: 'solid', reducedGlow: false });
  const [mode, setMode] = useState('night');
  const settings = useQuery({ queryKey: ['admin-settings'], queryFn: () => api('/api/v1/admin/settings').then((result) => result.data || []) });
  useEffect(() => {
    const saved = (settings.data || []).find((item) => item.key === 'map.identity')?.value;
    if (saved && typeof saved === 'object') setIdentity((current) => ({ ...current, ...saved }));
  }, [settings.data]);
  const payload = useMemo(() => JSON.stringify(identity), [identity]);
  const save = useMutation({
    mutationFn: () => api('/api/v1/admin/settings/map.identity', { method: 'PUT', body: JSON.stringify({ value: identity, description: 'Map marker identity and club ring defaults.' }) }),
    onSuccess: () => {
      localStorage.setItem('convoy.mapIdentity', payload);
      window.dispatchEvent(new Event('convoy:map-identity-changed'));
      toast.show('Map identity saved', 'success');
    },
    onError: (cause) => toast.show(cause.message, 'error'),
  });
  const update = (key, value) => setIdentity((current) => ({ ...current, [key]: value }));
  return <div className="workspace-page">
    <PageHeader eyebrow="Map system" title="Map Identity Studio" description="Customize club rings while safety icons stay consistent." actions={<Button variant="secondary" onClick={() => navigate('/settings')}><ArrowLeft size={14}/>Back</Button>}/>
    <div className="map-identity-layout workspace-fill">
      <section className="map-identity-controls">
        <div><label>Club accent</label><div className="identity-color-grid">{colors.map((color) => <button type="button" key={color} aria-label={color} className={identity.color === color ? 'active' : ''} style={{ background: color }} onClick={() => update('color', color)}/>)}</div></div>
        <div><label>Ring pattern</label><Select value={identity.pattern} onChange={(event) => update('pattern', event.target.value)}><option value="solid">Solid</option><option value="double">Double</option><option value="segmented">Segmented</option><option value="dashed">Dashed</option></Select></div>
        <div className="create-pass-toggle"><span><span><strong>Reduce glow</strong><small>Improves clarity in sunlight.</small></span></span><Toggle checked={identity.reducedGlow} onChange={(value) => update('reducedGlow', value)}/></div>
        <div className="grid grid-cols-2 gap-2"><Button variant="secondary" onClick={() => setIdentity({ color: '#42e8e0', pattern: 'solid', reducedGlow: false })}><RotateCcw size={14}/>Reset</Button><Button onClick={() => save.mutate()} disabled={save.isPending}><Save size={14}/>Save</Button></div>
      </section>
      <section className={`map-identity-preview map-identity-preview--${mode}`}>
        <div className="flex items-center justify-between"><div><p className="eyebrow">Live preview</p><h2>Role marker system</h2></div><div className="flex gap-2"><Button variant={mode === 'day' ? 'primary' : 'secondary'} className="h-8 px-3" onClick={() => setMode('day')}><Sun size={13}/></Button><Button variant={mode === 'night' ? 'primary' : 'secondary'} className="h-8 px-3" onClick={() => setMode('night')}><Moon size={13}/></Button></div></div>
        <div className="identity-preview-grid">{roles.map((role) => <MarkerPreview key={role} role={role} state="normal" identity={identity}/>)}</div>
        <div className="identity-state-row"><MarkerPreview role="member" state="speaking" identity={identity}/><MarkerPreview role="member" state="warning" identity={identity}/><MarkerPreview role="member" state="critical" identity={identity}/><MarkerPreview role="member" state="offline" identity={identity}/></div>
      </section>
    </div>
  </div>;
}
